import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, CheckCircle, Shield, Calculator, FileText, BarChart3, Wrench, Clock, Target, TrendingUp, Send } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

const serviceCategories = [
  {
    id: "tender-red-review",
    icon: Shield,
    title: "Tender Red Review",
    subtitle: "Pre-Submission Assurance",
    description: "Independent commercial preflight before your number goes out. One set of fresh eyes to identify scope gaps, duplicate allowances, quote exclusions, and commercial exposure.",
    services: [
      "Independent pre-submission review",
      "Scope-gap review",
      "Estimate-integrity review",
      "Quote and exclusion review",
      "Programme and commercial exposure review"
    ],
    cta: "Submit for Review",
    ctaLink: "/tender-red-review#tender-form",
    highlighted: true,
    image: "https://images.unsplash.com/photo-1608303588026-884930af2559?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnRzJTIwZXN0aW1hdGluZyUyMHRlbmRlciUyMGRvY3VtZW50cyUyMHBsYW5zfGVufDB8fHx8MTc4OTY5MzA5N3ww&ixlib=rb-4.1.0&q=85"
  },
  {
    id: "price-desk",
    icon: Send,
    title: "Price Desk",
    subtitle: "Managed Supplier Pricing",
    description: "Supplier quotes, RFQs, follow-ups, comparisons—managed and compiled so you can focus on the estimate and submission. You keep control; we handle the legwork.",
    services: [
      "Supplier sourcing & RFQs",
      "Quote follow-up & RFIs",
      "Quote comparison tables",
      "Estimate-ready pricing packs",
      "Exclusion & qualification review"
    ],
    cta: "Start a Request",
    ctaLink: "/price-desk#price-desk-form",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80"
  },
  {
    id: "cost-engineering",
    icon: Wrench,
    title: "Cost Engineering Services",
    subtitle: "Build-Up & Analysis",
    description: "Resource-based cost modelling, methodology analysis, and commercial scenario planning. Move beyond simple rates to understand what drives your project cost.",
    services: [
      "Resource-based cost build-ups",
      "Construction methodology analysis",
      "Labour and plant productivity",
      "Programme and cost integration",
      "Risk and uncertainty allowances",
      "Commercial scenario analysis"
    ],
    cta: "Discuss Requirements",
    ctaLink: "/#contact",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/4fol0kva_IMG_1579.JPG"
  },
  {
    id: "estimate-development",
    icon: Calculator,
    title: "Estimate Development",
    subtitle: "Quantities to Pricing",
    description: "From materials takeoffs and BOQs to full construction estimates. Clean numbers, clear boundaries, and outputs you can stand behind.",
    services: [
      "Materials takeoffs and BOQs",
      "Labour/programme takeoffs",
      "Full construction estimates",
      "Estimate review and reconciliation",
      "Tender pricing support"
    ],
    cta: "Get a Quote",
    ctaLink: "/#contact",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/o2qiosm3_Screenshot%202026-02-12%20151040.png"
  },
  {
    id: "cost-intelligence",
    icon: BarChart3,
    title: "Cost Intelligence",
    subtitle: "Data & Insights",
    description: "Turn historical data into forward insight. Rate analysis, benchmarking, and cost-driver identification to sharpen your competitive edge.",
    services: [
      "Rate and resource analysis",
      "Benchmarking",
      "Estimate-versus-actual analysis",
      "Historical cost feedback",
      "Cost-driver and sensitivity analysis"
    ],
    cta: "Learn More",
    ctaLink: "/#contact",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/zawnz8f6_IMG_3087.JPG"
  }
];

export default function ServicesPage() {
  usePageMeta("services");
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate("/#contact");
  };

  const handleCTA = (link) => {
    if (link.startsWith("/")) {
      navigate(link);
    } else {
      window.location.href = link;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={scrollToContact} />
      
      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-black text-white">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20 grayscale"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/rw8whfg7_rickydee_69839_Extend_deck_to_edge._Hyper_Realistic._7207206f-fb46-414d-a0a4-ce5fd11eeaa7.png')",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
              Construction Cost Engineering & Tender Assurance
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl uppercase mt-4 mb-6">
              What We Deliver
            </h1>
            <p className="font-body text-xl md:text-2xl text-zinc-300 max-w-3xl leading-relaxed">
              Independent cost engineering for small residential and civil projects in New Zealand and Australia. Clean numbers, clear boundaries, no mystery scope.
            </p>
          </div>
        </section>

        {/* Service Categories */}
        {serviceCategories.map((category, index) => (
          <section
            key={category.id}
            id={category.id}
            className={`py-16 md:py-24 border-t-4 border-black ${
              index % 2 === 0 ? "bg-white" : "bg-zinc-50"
            }`}
            data-testid={`service-section-${category.id}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className={`grid md:grid-cols-2 gap-12 md:gap-16 items-start`}>
                {/* Content */}
                <div className={index % 2 === 1 ? "md:order-2" : ""}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 border-2 flex items-center justify-center ${
                      category.highlighted ? "bg-black text-white border-black" : "border-black"
                    }`}>
                      <category.icon size={28} strokeWidth={1.5} />
                    </div>
                    {category.highlighted && (
                      <span className="font-mono text-xs uppercase tracking-widest bg-black text-white px-3 py-1">
                        Flagship Service
                      </span>
                    )}
                  </div>

                  <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mb-2">
                    {category.title}
                  </h2>
                  <p className="font-mono text-sm uppercase tracking-widest text-zinc-500 mb-6">
                    {category.subtitle}
                  </p>
                  
                  <p className="font-body text-lg text-zinc-700 mb-8 leading-relaxed">
                    {category.description}
                  </p>

                  {/* Services List */}
                  <div className="mb-8">
                    <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
                      Services Include:
                    </h3>
                    <ul className="space-y-3">
                      {category.services.map((service, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle size={18} className="text-black mt-0.5 flex-shrink-0" />
                          <span className="font-body text-zinc-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button
                      onClick={() => handleCTA(category.ctaLink)}
                      className={`h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 flex items-center gap-2 btn-brutal ${
                        category.highlighted
                          ? "bg-black text-white border-black"
                          : "bg-white text-black border-black hover:bg-zinc-100"
                      }`}
                      data-testid={`service-cta-${category.id}`}
                    >
                      {category.cta}
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>

                {/* Image */}
                <div className={`border-4 border-black overflow-hidden ${index % 2 === 1 ? "md:order-1" : ""}`}>
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-80 md:h-96 object-cover grayscale"
                  />
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Pricing Note */}
        <section className="py-12 border-t-4 border-black bg-zinc-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h3 className="font-heading font-bold text-xl uppercase mb-4">Pricing</h3>
            <p className="font-body text-zinc-600 mb-2">
              <strong>Tender Red Review:</strong> NZ$595 (New Zealand) / A$545 (Australia) excl. GST for standard founding reviews.
            </p>
            <p className="font-body text-zinc-600">
              <strong>All other services:</strong> Quoted per project based on scope and complexity. All prices exclude GST.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mb-6">
              Not Sure What You Need?
            </h2>
            <p className="font-body text-zinc-400 mb-8 max-w-2xl mx-auto">
              Send through the plans and let us know what you're trying to achieve. We'll recommend the right output and confirm the fee before we start.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-white text-black h-14 px-8 uppercase font-bold tracking-widest border-2 border-white btn-brutal inline-flex items-center gap-3 hover:bg-zinc-200"
              data-testid="services-cta"
            >
              Send Plans / Get Quote
              <ArrowRight size={20} />
            </button>
            
            {/* Boundary Statement */}
            <p className="mt-8 font-body text-zinc-500 text-sm max-w-2xl mx-auto">
              Blackline Estimating provides construction cost engineering and tender assurance services. Our services do not replace project-specific professional advice from registered engineers, architects, quantity surveyors, or legal professionals. Blackline is not a registered QS practice or certified cost-engineering organisation.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
